import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminPaymentAgentsAPI, fmtError } from '@/lib/api';
import { CheckCircle2, XCircle, Trash2, ArrowLeft, Eye } from 'lucide-react';

const ID_LABEL = {
  passport: 'International Passport',
  drivers_license: "Driver's License",
  national_id: 'National ID',
};

export default function AdminPaymentAgentApplications() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null);   // {b64, id_type, name}

  const load = async () => {
    try { setRows(await adminPaymentAgentsAPI.apps(statusFilter)); setErr(''); }
    catch (e) { setErr(fmtError(e)); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const openPreview = async (r) => {
    try {
      const img = await adminPaymentAgentsAPI.appImage(r.application_id);
      setPreview({ ...img, name: r.name });
    } catch (e) { setErr(fmtError(e)); }
  };

  const approve = async (id) => {
    if (!window.confirm('Approve this application? An active payment agent will be created.')) return;
    try { await adminPaymentAgentsAPI.approveApp(id); load(); }
    catch (e) { setErr(fmtError(e)); }
  };

  const reject = async (id) => {
    const notes = window.prompt('Optional: reason for rejection (visible only to admins).', '');
    if (notes === null) return;
    try { await adminPaymentAgentsAPI.rejectApp(id, notes); load(); }
    catch (e) { setErr(fmtError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Permanently delete this application?')) return;
    try { await adminPaymentAgentsAPI.deleteApp(id); load(); }
    catch (e) { setErr(fmtError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/payment-agents" className="btn-ghost btn-sm" data-testid="back-to-agents-admin"><ArrowLeft size={14} /> Agents</Link>
          <h1 className="font-display text-3xl">Agent applications</h1>
        </div>
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`btn-ghost btn-sm ${statusFilter === s ? 'text-[#00ff66] border-[#00ff66]/30' : ''}`} data-testid={`apps-filter-${s}`}>
              {s.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="text-sm text-[#ff3366]">{err}</div>}

      <div className="card-tech rounded-lg overflow-x-auto">
        <div className="min-w-[1000px]">
          <div className="grid grid-cols-[1.5fr_1fr_1fr_1.2fr_1fr_1fr_1.4fr] gap-3 px-4 py-3 label-xs border-b border-white/5">
            <div>NAME / EMAIL</div><div>COUNTRY</div><div>ID TYPE</div><div>METHODS</div><div>SUBMITTED</div><div>STATUS</div><div>ACTIONS</div>
          </div>
          {rows.length === 0 && <div className="px-4 py-6 text-sm text-zinc-500">No {statusFilter} applications.</div>}
          {rows.map(r => (
            <div key={r.application_id} className="grid grid-cols-[1.5fr_1fr_1fr_1.2fr_1fr_1fr_1.4fr] gap-3 px-4 py-3 border-b border-white/5 text-sm items-center" data-testid={`app-row-${r.application_id}`}>
              <div className="min-w-0">
                <div className="text-white truncate">{r.name}</div>
                <div className="text-xs text-zinc-500 truncate" title={r.email}>{r.email}</div>
              </div>
              <div className="text-zinc-400">{r.country}</div>
              <div className="text-xs text-zinc-400">{ID_LABEL[r.id_type] || r.id_type}</div>
              <div className="text-xs text-zinc-400 truncate">{(r.methods || []).join(' · ')}</div>
              <div className="text-xs font-mono text-zinc-400">{new Date(r.created_at).toLocaleDateString()}</div>
              <div>
                <span className={`chip ${r.status === 'pending' ? 'chip-yellow' : r.status === 'approved' ? 'chip-green' : 'chip-red'}`}>{r.status.toUpperCase()}</span>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <button className="btn-ghost btn-sm" onClick={() => openPreview(r)} data-testid={`app-id-view-${r.application_id}`}>
                  <Eye size={12} /> ID
                </button>
                {r.status === 'pending' && (
                  <>
                    <button className="btn-ghost btn-sm text-[#00ff66]" onClick={() => approve(r.application_id)} data-testid={`app-approve-${r.application_id}`}>
                      <CheckCircle2 size={12} /> Approve
                    </button>
                    <button className="btn-ghost btn-sm text-[#ff3366]" onClick={() => reject(r.application_id)} data-testid={`app-reject-${r.application_id}`}>
                      <XCircle size={12} /> Reject
                    </button>
                  </>
                )}
                <button className="btn-ghost btn-sm text-[#ff3366]" onClick={() => remove(r.application_id)}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="id-preview-modal">
          <div className="card-tech rounded-lg p-4 max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center">
              <div>
                <div className="font-display text-xl">{preview.name}</div>
                <div className="text-xs text-zinc-500">{ID_LABEL[preview.id_type] || preview.id_type}</div>
              </div>
              <button onClick={() => setPreview(null)} className="text-zinc-500 hover:text-white">Close</button>
            </div>
            {preview.id_image_b64 ? (
              <img
                src={`data:image/jpeg;base64,${preview.id_image_b64}`}
                alt="ID document"
                className="mt-4 max-h-[70vh] w-full object-contain rounded-md border border-white/10"
              />
            ) : <div className="text-sm text-zinc-500 mt-4">No ID image found.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
